<template>
    <Modal
        ref="modalRef"
        :primary-button-disabled="!form.isDirty"
        :primary-button-label="!maintainer ? trans('general.action.add') : trans('general.action.save')"
        :processing="form.processing"
        :title="title"
        max-width="4xl"
        @submit="submitForm"
    >
        <form @submit.prevent="submitForm">
            <div class="flex flex-col divide-y divide-gray-200">
                <section class="pb-6 first:pt-0">
                    <row class="g-4">
                        <column md="4">
                            <div class="text-base leading-snug font-semibold text-body">
                                {{ trans('maintainers.form.company_name.label') }}
                                <span class="text-danger">*</span>
                            </div>
                            <p class="mb-0 text-sm leading-6 text-muted">
                                {{ trans('maintainers.form.company_name.placeholder') }}
                            </p>
                        </column>
                        <column md="8">
                            <div class="rounded-card border border-panel-border bg-panel p-4">
                                <jet-input
                                    v-model="form.name"
                                    :autofocus="true"
                                    :horizontal="false"
                                    :invalidFeedback="form.errors.name"
                                    :label="trans('maintainers.form.company_name.label')"
                                    :placeholder="trans('maintainers.form.company_name.placeholder')"
                                    :required="true"
                                    class="mb-0"
                                >
                                    <template #prepend>
                                        <icon name="cil-building" />
                                    </template>
                                </jet-input>
                            </div>
                        </column>
                    </row>
                </section>

                <LanguageSelectSection v-model="form.language" :error="form.errors.language" class="py-6" />

                <section class="py-6">
                    <row class="g-4">
                        <column md="4">
                            <div class="text-base leading-snug font-semibold text-body">
                                {{ trans('maintainers.form.business_contacts.label') }}
                            </div>
                            <p class="mb-0 text-sm leading-6 text-muted">
                                {{ trans('maintainers.form.business_contacts.description') }}
                            </p>
                            <jet-button class="mt-3" type="button" variant="secondary" @click="addBusinessContact">
                                {{ trans('stakeholders.form.email.add.label') }}
                                <icon icon="cil-plus" />
                            </jet-button>
                        </column>
                        <column md="8">
                            <div class="flex flex-col gap-3">
                                <div v-for="(contact, index) in form.businessContacts" :key="contact.localKey">
                                    <StakeholderContactCard
                                        :email="contact.email"
                                        :errors="{
                                            name: businessContactErrors[contact.localKey]?.name,
                                            role: businessContactErrors[contact.localKey]?.role,
                                            email: businessContactErrors[contact.localKey]?.email,
                                            phone: businessContactErrors[contact.localKey]?.phone,
                                        }"
                                        :name="contact.name"
                                        :phone="contact.phone"
                                        :placeholders="contactCardPlaceholders"
                                        :required="{
                                            name: true,
                                            role: false,
                                            email: true,
                                            phone: false,
                                        }"
                                        :role="contact.role"
                                        @update:email="form.businessContacts[index].email = $event"
                                        @update:name="form.businessContacts[index].name = $event"
                                        @update:phone="form.businessContacts[index].phone = $event"
                                        @update:role="form.businessContacts[index].role = $event"
                                    >
                                        <template #actions>
                                            <jet-button
                                                v-if="form.businessContacts.length > 1"
                                                v-c-tooltip="{ content: trans('general.action.delete') }"
                                                :destructive="true"
                                                type="button"
                                                variant="secondary"
                                                class="min-w-0"
                                                @click="removeBusinessContact(index)"
                                            >
                                                <icon name="cil-trash" />
                                            </jet-button>
                                        </template>
                                    </StakeholderContactCard>
                                </div>
                            </div>
                        </column>
                    </row>
                </section>

                <section class="pt-6">
                    <row class="g-4">
                        <column md="4">
                            <div class="text-base leading-snug font-semibold text-body">
                                {{ trans('maintainers.form.support_contacts.label') }}
                            </div>
                            <p class="mb-0 text-sm leading-6 text-muted">
                                {{ trans('maintainers.form.support_contacts.description') }}
                            </p>
                            <jet-button class="mt-3" type="button" variant="secondary" @click="addSupportContact">
                                {{ trans('stakeholders.form.email.add.label') }}
                                <icon icon="cil-plus" />
                            </jet-button>
                        </column>
                        <column md="8">
                            <div class="flex flex-col gap-3">
                                <div v-for="(contact, index) in form.supportContacts" :key="contact.localKey">
                                    <StakeholderContactCard
                                        :email="contact.email"
                                        :errors="{
                                            name: supportContactErrors[contact.localKey]?.name,
                                            role: supportContactErrors[contact.localKey]?.role,
                                            email: supportContactErrors[contact.localKey]?.email,
                                            phone: supportContactErrors[contact.localKey]?.phone,
                                        }"
                                        :is-informational="contact.isInformational"
                                        :informational-tooltip="trans('stakeholders.form.support_contacts.informational.tooltip')"
                                        :name="contact.name"
                                        :phone="contact.phone"
                                        :placeholders="contactCardPlaceholders"
                                        :show-informational-checkbox="true"
                                        :role="contact.role"
                                        @update:email="form.supportContacts[index].email = $event"
                                        @update:isInformational="form.supportContacts[index].isInformational = $event"
                                        @update:name="form.supportContacts[index].name = $event"
                                        @update:phone="form.supportContacts[index].phone = $event"
                                        @update:role="form.supportContacts[index].role = $event"
                                    >
                                        <template #actions>
                                            <jet-button
                                                v-c-tooltip="{ content: trans('general.action.delete') }"
                                                :destructive="true"
                                                type="button"
                                                variant="secondary"
                                                class="min-w-0"
                                                @click="removeSupportContact(index)"
                                            >
                                                <icon name="cil-trash" />
                                            </jet-button>
                                        </template>
                                    </StakeholderContactCard>
                                </div>
                                <div
                                    v-if="form.supportContacts.length === 0"
                                    class="rounded-card border border-panel-border bg-panel p-4 text-sm text-muted"
                                >
                                    {{ trans('maintainers.form.support_contacts.label') }}
                                </div>
                            </div>
                        </column>
                    </row>
                </section>
            </div>
        </form>
        <row class="g-4 mt-4">
            <column md="4" />
            <column md="8">
                <required-fields />
            </column>
        </row>

        <div v-if="form.supportContacts.length > 0" class="mt-4 rounded-card border border-primary/20 bg-primary/5 p-4">
            <JetCheckbox
                v-model="form.supportContactsConsentAccepted"
                :invalid-feedback="form.errors.supportContactsConsentAccepted"
                :label="trans('maintainers.form.support_contacts.consent')"
                @update:model-value="onConsentToggled"
            />
        </div>
    </Modal>
</template>

<script setup>
import Modal from '@/Components/Modal.vue';
import RequiredFields from '@/Components/RequiredFields.vue';
import JetButton from '@/Jetstream/Button.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetInput from '@/Jetstream/Input.vue';
import { trans } from '@/mixins/i18n.js';
import LanguageSelectSection from '@/Pages/Stakeholders/Components/LanguageSelectSection.vue';
import StakeholderContactCard from '@/Pages/Stakeholders/Components/StakeholderContactCard.vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { computed, ref } from 'vue';

const modalRef = ref(null);
const emit = defineEmits(['created', 'updated']);

const props = defineProps({
    maintainer: Object,
});

const page = usePage();

const contactCardPlaceholders = {
    name: trans('stakeholders.form.contact_name.placeholder'),
    role: trans('stakeholders.form.description.placeholder'),
    email: trans('stakeholders.form.email.placeholder'),
    phone: trans('stakeholders.form.phone.placeholder'),
};

let contactKeyCounter = 0;

const createContactLocalKey = (type) => `${type}-contact-${contactKeyCounter++}`;

const emptyContact = () => ({
    id: null,
    name: '',
    role: '',
    email: '',
    phone: '',
});

const emptyBusinessContact = () => ({
    ...emptyContact(),
    localKey: createContactLocalKey('business'),
});

const emptySupportContact = () => ({
    ...emptyContact(),
    isInformational: false,
    localKey: createContactLocalKey('support'),
});

const businessContacts = props.maintainer?.businessContacts?.length
    ? props.maintainer.businessContacts.map((contact) => ({
          id: contact.id,
          name: contact.name,
          role: contact.role ?? '',
          email: contact.email ?? '',
          phone: contact.phone ?? '',
          localKey: createContactLocalKey('business'),
      }))
    : [emptyBusinessContact()];

const supportContacts = props.maintainer?.supportContacts?.length
    ? props.maintainer.supportContacts.map((contact) => ({
          id: contact.id,
          name: contact.name,
          role: contact.role ?? '',
          email: contact.email ?? '',
          phone: contact.phone ?? '',
          isInformational: contact.isInformational,
          localKey: createContactLocalKey('support'),
      }))
    : [];

const form = useForm({
    name: props.maintainer?.name ?? '',
    language: props.maintainer?.language || page.props.locale,
    businessContacts,
    supportContacts,
    supportContactsConsentAccepted: props.maintainer?.allSupportContactsVerified ?? false,
});

const businessContactErrors = ref({});
const supportContactErrors = ref({});

const title = computed(() => {
    return props.maintainer ? trans('maintainers.edit.title') : trans('maintainers.create.title');
});

const addBusinessContact = () => {
    form.businessContacts.push(emptyBusinessContact());
};

const removeBusinessContact = (index) => {
    form.businessContacts.splice(index, 1);
};

const addSupportContact = () => {
    form.supportContacts.push(emptySupportContact());
};

const removeSupportContact = (index) => {
    form.supportContacts.splice(index, 1);
};

const onConsentToggled = (accepted) => {
    if (accepted) {
        form.clearErrors('supportContactsConsentAccepted');
    }
};

const mapContactErrors = (errors, field, localKeys) => {
    return localKeys.reduce((carry, localKey, index) => {
        const mappedErrors = {
            name: errors[`${field}.${index}.name`],
            role: errors[`${field}.${index}.role`],
            email: errors[`${field}.${index}.email`],
            phone: errors[`${field}.${index}.phone`],
        };

        if (Object.values(mappedErrors).some(Boolean)) {
            carry[localKey] = mappedErrors;
        }

        return carry;
    }, {});
};

const submitForm = () => {
    const url = props.maintainer ? route('maintainers.update', { maintainer: props.maintainer }) : route('maintainers.store');

    const submittedBusinessContactKeys = form.businessContacts.map((contact) => contact.localKey);
    const submittedSupportContactKeys = form.supportContacts.map((contact) => contact.localKey);
    businessContactErrors.value = {};
    supportContactErrors.value = {};
    form.clearErrors();

    form.transform((data) => {
        const transformed = {
            name: data.name,
            language: data.language,
            businessContacts: data.businessContacts.map(({ localKey, ...contact }) => contact),
            supportContacts: data.supportContacts.map(({ localKey, ...contact }) => contact),
            supportContactsConsentAccepted: data.supportContactsConsentAccepted,
        };

        if (props.maintainer) {
            transformed._method = 'PUT';
        }

        return transformed;
    }).post(url, {
        preserveScroll: true,
        onError: (errors) => {
            businessContactErrors.value = mapContactErrors(errors, 'businessContacts', submittedBusinessContactKeys);
            supportContactErrors.value = mapContactErrors(errors, 'supportContacts', submittedSupportContactKeys);
        },
        onSuccess: () => {
            emit(props.maintainer ? 'updated' : 'created', props.maintainer);
            modalRef.value?.close();
        },
    });
};
</script>
